import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { AgmCoreModule, GoogleMapsAPIWrapper } from '@agm/core';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgHttpLoaderModule } from 'ng-http-loader';
import { NgxDnDModule } from '@swimlane/ngx-dnd';
import { DateFormatPipe, MomentModule } from 'ngx-moment';
import { FileUploadModule } from 'ng2-file-upload/ng2-file-upload';
import { InternationalPhoneNumberModule } from 'ng-phone-number';
import { TitleCasePipe } from '@angular/common';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { MenuComponent } from './components/menu/menu.component';
import { LoginComponent } from './components/login/login.component';
import { environment } from '../environments/environment';
import { UserListComponent } from './components/users/user-list/user-list.component';
import { FilterPipe } from './pipes/filter.pipe';
import { I18n } from './services/custom-datepicker-i18n.service';
import { UserProfileComponent } from './components/users/user-profile/user-profile.component';
import {
  UserProfileIntercomRedirectComponent
} from './components/users/user-profile-intercom-redirect/user-profile-intercom-redirect.component';
import {
  UserProfileContactInformationComponent
} from './components/users/user-profile/user-profile-contact-information/user-profile-contact-information.component';
import {
  UserProfileInvoicesComponent
} from './components/users/user-profile/user-profile-invoices/user-profile-invoices.component';
import { UserProfileOrdersComponent } from './components/users/user-profile/user-profile-orders/user-profile-orders.component';
import {
  UserProfileSendMessageModalComponent
} from './components/modals/user-profile-send-message-modal/user-profile-send-message-modal.component';
import {
  UserProfilePerformanceComponent
} from './components/users/user-profile/user-profile-performance/user-profile-performance.component';
import { OrdersComponent } from './components/orders/orders.component';
import { OrderDetailsComponent } from './components/orders/order-details/order-details.component';
import { ClickStopPropagationDirective } from './directives/click-stop-propagation.directive';
import { GetValuesPipe } from './pipes/get-values.pipe';
import { EnumToArrayPipe } from '../lib/lib-ngx/pipes/enum-to-array.pipe';
import { SideMenuComponent } from './components/side-menu/side-menu.component';
import { HttpErrorInterceptorService } from '../lib/lib-ngx/services/http-error-interceptor.service';
import { ArticleFamilyComponent } from './components/article/article-family/article-family.component';
import { ArticleSettingComponent } from './components/article/article-setting/article-setting.component';
import { ArticleListComponent } from './components/article/article-list/article-list.component';
import { NewArticleComponent } from './components/article/new-article/new-article.component';
import { ArticlePricesComponent } from './components/article/article-prices/article-prices.component';
import { ArticleDetailsComponent } from './components/article/article-details/article-details.component';
import { ArticlePackListComponent } from './components/article/article-pack-list/article-pack-list.component';
import { ArticlePackEditComponent } from './components/article/article-pack-edit/article-pack-edit.component';
import { ArticleOptionTypeComponent } from './components/article/article-setting/article-option-type/article-option-type.component';
import { ArticleOptionComponent } from './components/article/article-setting/article-option/article-option.component';
import { LmUploadPipe } from './pipes/lm-upload.pipe';
import { ImagePreviewDirective } from './directives/image-preview.directive';
import { LavandierListComponent } from './components/lavandier/lavandier-list/lavandier-list.component';
import { LavandierDetailsComponent } from './components/lavandier/lavandier-details/lavandier-details.component';
import { NewLavandierComponent } from './components/lavandier/new-lavandier/new-lavandier.component';
import { UserTypeSettingComponent } from './components/lavoirmoderne/user-type-setting/user-type-setting.component';
import { UserTypeEditComponent } from './components/lavoirmoderne/user-type-edit/user-type-edit.component';
import { EnumNumberValueToArrayPipe } from './pipes/enum-number-value-to-array.pipe';
import { ConfirmationModalComponent } from './components/modals/confirmation-modal/confirmation-modal.component';
import {
  OrderShipReturnValidateModalComponent
} from './components/modals/order-ship-return-validate-modal/order-ship-return-validate-modal.component';
import { ImagePreviewModalComponent } from './components/modals/image-preview-modal/image-preview-modal.component';
import { ConfigService } from '../lib/lib-ngx/services/config.service';
import { DiscountCodeListComponent } from './components/users/discount-code-list/discount-code-list.component';
import { DiscountCodeAddComponent } from './components/users/discount-code-add/discount-code-add.component';
import { RoleListComponent } from './components/lavandier/role-list/role-list.component';
import { RoleDetailsComponent } from './components/lavandier/role-details/role-details.component';
import { RoleAddComponent } from './components/lavandier/role-add/role-add.component';
import {
  UserDataVerificationModalComponent
} from './components/modals/user-data-verification-modal/user-data-verification-modal.component';
import { ManageAddressModalComponent } from './components/modals/manage-address-modal/manage-address-modal.component';
import { UserCreditComponent } from './components/users/user-profile/user-credit/user-credit.component';
import { UserCreditEditFormComponent } from './components/users/user-profile/user-credit/user-credit-edit-form/user-credit-edit-form.component';
import { CalendarComponent } from './components/store/calendar/calendar.component';
import { EditCalendarDayModalComponent } from './components/modals/edit-calendar-day-modal/edit-calendar-day-modal.component';
import { EditAccountComponent } from './components/account/edit-account/edit-account.component';
import { EditUserComponent } from './components/user/edit-user/edit-user.component';
import { AddPaymentModalComponent } from './components/modals/add-payment-modal/add-payment-modal.component';
import { EventConfigEditModalComponent } from './components/modals/event-config-edit-modal/event-config-edit-modal.component';
import { ShippingPriceEditModalComponent } from './components/modals/shipping-price-edit-modal/shipping-price-edit-modal.component';
import { HasFlagsPipe } from '../lib/lib-ngx/pipes/has-flags.pipe';
import { ChronopostTrackingUrlPipe } from '../lib/lib-ngx/pipes/chronopost-tracking-url.pipe';
import { APP_TOKEN, appInitializerFn } from '../lib/lib-ngx/services/AppInitializerFn';
import { VersionService } from '../lib/lib-ngx/services/version.service';
import { HttpInterceptorVersionService } from '../lib/lib-ngx/services/http-interceptor-version.service';
import { QRCodeAPIKeyModalComponent } from './components/modals/qrcode-apikey-modal/qrcode-apikey-modal.component';
import { OrderTraceabilityComponent } from './components/common/order-traceability/order-traceability.component';
import { UserProfileKitBatchComponent } from './components/users/user-profile/user-profile-kit-batch/user-profile-kit-batch.component';
import { OrderShipReturnListComponent } from './components/order/order-ship-return-list/order-ship-return-list.component';
import { OrderCreateCreditComponent } from './components/order/order-create-credit/order-create-credit.component';
import { UserTransportSheetComponent } from './components/users/user-profile/user-transport-sheet/user-transport-sheet.component';
import { ControlResetTimerDirective } from '../lib/lib-ngx/directives/control-reset-timer.directive';
import { PtlComponent } from './components/ptl/ptl.component';
import { NewUserComponent } from './components/lavandier/new-lavandier/new-user/new-user.component';
import { PickupSelectModalComponent } from './components/modals/pickup-select-modal/pickup-select-modal.component';
import { IsCreditCancelledPipe } from './components/users/user-profile/user-credit/is-credit-cancelled.pipe';

declare function require(moduleName: string): any;

export function createTranslateLoader(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, [
    {prefix: './assets/lib-ngx/i18n/', suffix: '.json?cb=' + new Date().getTime()},
  ]);
}

// todo change when upgrading angular version https://stackoverflow.com/a/48869478
export const npm = require('../../package.json');

@NgModule({
  declarations: [
    AppComponent,
    MenuComponent,
    LoginComponent,
    UserListComponent,
    FilterPipe,
    UserProfileComponent,
    UserProfileIntercomRedirectComponent,
    UserProfileContactInformationComponent,
    UserProfileInvoicesComponent,
    UserProfileOrdersComponent,
    UserProfileSendMessageModalComponent,
    UserProfilePerformanceComponent,
    OrdersComponent,
    OrderDetailsComponent,
    ArticleDetailsComponent,
    ClickStopPropagationDirective,
    GetValuesPipe,
    EnumToArrayPipe,
    SideMenuComponent,
    ArticleFamilyComponent,
    ArticleSettingComponent,
    ArticleListComponent,
    NewArticleComponent,
    ArticlePackListComponent,
    ArticlePackEditComponent,
    ArticleOptionTypeComponent,
    ArticleOptionComponent,
    LmUploadPipe,
    ImagePreviewDirective,
    LavandierListComponent,
    LavandierDetailsComponent,
    NewLavandierComponent,
    ArticlePricesComponent,
    UserTypeSettingComponent,
    UserTypeEditComponent,
    EnumNumberValueToArrayPipe,
    ConfirmationModalComponent,
    ImagePreviewModalComponent,
    DiscountCodeListComponent,
    DiscountCodeAddComponent,
    RoleListComponent,
    RoleDetailsComponent,
    RoleAddComponent,
    UserDataVerificationModalComponent,
    ManageAddressModalComponent,
    UserCreditComponent,
    UserCreditEditFormComponent,
    CalendarComponent,
    EditCalendarDayModalComponent,
    EditAccountComponent,
    EditUserComponent,
    AddPaymentModalComponent,
    EventConfigEditModalComponent,
    ShippingPriceEditModalComponent,
    HasFlagsPipe,
    ChronopostTrackingUrlPipe,
    QRCodeAPIKeyModalComponent,
    OrderTraceabilityComponent,
    UserProfileKitBatchComponent,
    OrderShipReturnListComponent,
    OrderCreateCreditComponent,
    UserTransportSheetComponent,
    ControlResetTimerDirective,
    PtlComponent,
    NewUserComponent,
    PickupSelectModalComponent,
    OrderShipReturnValidateModalComponent,
    IsCreditCancelledPipe,
  ],
  imports: [
    BrowserModule,
    NgbModule.forRoot(),
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AgmCoreModule.forRoot({
      apiKey: environment.api_key_google,
      libraries: ['places']
    }),
    NgxDatatableModule,
    NgHttpLoaderModule,
    NgxDnDModule,
    MomentModule,
    FileUploadModule,
    InternationalPhoneNumberModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    })
  ],
  providers: [
    I18n,
    GoogleMapsAPIWrapper,
    {provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptorService, multi: true},
    DateFormatPipe,
    TitleCasePipe,
    IsCreditCancelledPipe,
    {provide: APP_TOKEN, useValue: {name: npm.name, version: npm.version}},
    {provide: APP_INITIALIZER, useFactory: appInitializerFn, multi: true, deps: [ConfigService, VersionService, APP_TOKEN]},
    {provide: HTTP_INTERCEPTORS, useClass: HttpInterceptorVersionService, multi: true},
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    UserProfileSendMessageModalComponent,
    UserProfilePerformanceComponent,
    ConfirmationModalComponent,
    ImagePreviewModalComponent,
    UserDataVerificationModalComponent,
    ManageAddressModalComponent,
    EditCalendarDayModalComponent,
    AddPaymentModalComponent,
    EventConfigEditModalComponent,
    ShippingPriceEditModalComponent,
    QRCodeAPIKeyModalComponent,
    PickupSelectModalComponent,
    OrderShipReturnValidateModalComponent,
  ]
})
export class AppModule {
}
